import React, { ReactNode } from 'react';
import cn from 'classnames';
import { SortDirection } from 'libs/end-to-end/src/ui-types';
import { TableSortingIcon } from 'apps/sigma2-ui/src/components/ui/TableSortIcon';
import { TableFilterIconSvg } from 'apps/sigma2-ui/src/components/svg/TableFilterIcon';
import { Menu } from '@headlessui/react';
import { S2Checkbox } from 'apps/sigma2-ui/src/components/ui/Checkbox';
import { ICheckbox } from 'apps/sigma2-ui/src/components/ui/CheckboxGroup';
import { SelectAllCheckbox } from 'apps/sigma2-ui/src/components/ui/SelectAllCheckbox';
import { SortedTableColumn } from 'apps/sigma2-ui/src/lib/hooks/useTableSorting';
import { Omit } from '@react-spring/web';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { Spinner } from 'apps/sigma2-ui/src/components/ui/Spinner';

export interface ColumnDefinition<T extends object> {
  /**
   * Used for having a unique key for each column and for sorting. Make this the path to the value.
   * @example 'id' {id: '123'}
   * @example 'budget.recommended' {budget: {recommended: 100000}}
   */
  id: string;
  /**
   * The header to display for the column.
   * @example () => 'ID'
   * @example () => <span>ID</span>
   */
  header: () => ReactNode;

  /**
   * The content to display in the cell.
   * @example ({ item }) => item.id
   * @example ({ item }) => <span>{item.id}</span>
   */
  cell: (params: { item: T; index: number; rowIndex: number }) => ReactNode;

  /**
   * The class names to add to the cell.
   * @example ({ item }) => null
   * @example ({ item }) => 'align-top'
   */
  cellClassNames?: (params: { item: T; index: number }) => string | null;
}

interface TableProps<T extends object> {
  /**
   * The list of items to display in the table as rows.
   */
  items: T[];
  /**
   * Describes how to render the value for each column in the table. The API is similar to the
   * AWS Design System, Cloud Component Library, Table component.
   * @url https://cloudscape.design/components/table/?tabId=playground
   */
  columnDefinitions: ColumnDefinition<T>[];
  // Boolean to display the header
  displayHeader?: boolean;
  /**
   * The content to display in the footer.
   * @default null
   */
  footer?: ReactNode;
  /**
   * The content to display when the table is empty.
   * @default <div className="text-gray-900 text-center">No results</div>
   */
  empty?: ReactNode;
  /**
   * The content to display while the table is loading.
   * @default <CircularProgress className="text-secondary" size={30} />
   */
  loading?: ReactNode;
  /**
   * Whether or not the table is loading.
   * @default false
   */
  isLoading?: boolean;
  refs?: {
    /**
     * Optional ref to the last row in the table. This is useful for infinite scrolling.
     * @see /Table.stories.tsx
     */
    lastRow?: React.RefCallback<HTMLTableRowElement | null>;
    /**
     * Optional ref to the tableContainer. This is useful for infinite scrolling.
     * @see /Table.stories.tsx
     */
    tableContainer?: React.MutableRefObject<HTMLDivElement | null>;
  };
  classNames?: {
    /**
     * Wraps the table, but not the footer. Great for making the table scrollable while keeping the footer visible
     * with the number of items visible versus total.
     */
    tableContainer?: string;
    outerContainer?: string;
    rows?: string;
  };
}

const LoadingTableRow: React.FC<{ colSpan: number; children: ReactNode }> = ({
  colSpan,
  children,
}) => {
  return (
    <tr className="odd:bg-white">
      <td colSpan={colSpan} className="text-center px-2 pb-2 pt-4">
        {children}
      </td>
    </tr>
  );
};

const EmptyTableRow: React.FC<{ colSpan: number; children: ReactNode }> = ({
  colSpan,
  children,
}) => {
  return (
    <tr className="odd:bg-white">
      <td colSpan={colSpan} className="text-center px-2 py-2 font-sans">
        {children}
      </td>
    </tr>
  );
};

const TableRows = <T extends object>({
  items,
  columnDefinitions,
  lastRowRef,
  className,
}: {
  items: T[];
  columnDefinitions: ColumnDefinition<T>[];
  lastRowRef?: React.Ref<HTMLTableRowElement>;
  className: string;
}): React.ReactElement => {
  const cellRefs = React.useMemo(() => {
    return items.map(() =>
      columnDefinitions.map(() => React.createRef<HTMLDivElement>()),
    );
  }, [items, columnDefinitions]);

  const numItems = items.length;
  return (
    <>
      {items.map((item, rowIndex) => {
        const isLastRow = rowIndex === numItems - 1;

        return (
          <tr
            key={rowIndex}
            className={cn(className)}
            ref={isLastRow ? lastRowRef : undefined}
          >
            {columnDefinitions.map((columnDefinition, index) => {
              const additionalClassNames =
                columnDefinition.cellClassNames?.({ item, index }) ?? null;

              const cellRef = cellRefs[rowIndex][index];

              const isEven = rowIndex % 2 === 0;
              return (
                <td
                  key={index}
                  className={cn(
                    'px-2 py-2 text-gray-900',
                    index === 0 && 'sticky left-0 z-[1]',
                    isEven ? 'bg-white' : 'bg-gray-100',
                    additionalClassNames,
                  )}
                >
                  <div ref={cellRef} className={cn('relative inline-block')}>
                    {columnDefinition.cell({ item, index, rowIndex })}
                  </div>
                </td>
              );
            })}
          </tr>
        );
      })}
    </>
  );
};

const toggleSortDirection = (currentDirection: SortDirection): SortDirection =>
  currentDirection === 'desc' ? 'asc' : 'desc';

export const SortableTableHeaderCell: React.FC<{
  children: ReactNode;
  id: string;
  isActive: boolean;
  direction: SortDirection;
  onSortChange: (params: SortedTableColumn[]) => void;
  onClick?: () => void;
}> = ({ children, id, isActive, direction, onSortChange, onClick }) => {
  return (
    <div className="text-left py-1 font-bold">
      <button
        className={cn('flex items-center space-x-1 cursor-pointer')}
        onClick={() => {
          onClick?.();

          onSortChange([
            {
              id,
              // Only change direction if the column is already active.
              direction: isActive ? toggleSortDirection(direction) : direction,
            },
          ]);
        }}
      >
        {children}
        <span>
          <TableSortingIcon direction={direction} isActive={isActive} />
        </span>
      </button>
    </div>
  );
};

interface TablePaginationProps {
  numVisibleItems: number;
  numTotalItems: number;
}

export const TablePaginationFooter: React.FC<TablePaginationProps> = ({
  numVisibleItems,
  numTotalItems,
}) => {
  const language = useLanguage();

  return (
    <p className="font-sans text-gray-700">
      {language.tablePaginationFooter({ numTotalItems, numVisibleItems })}
    </p>
  );
};

/**
 * Renders a checkbox group that can be use to toggle the visibility of the values shown in this column.
 */
export const FilterableTableHeaderCell: React.FC<{
  children: ReactNode;
  checkboxes: ICheckbox[];
  // We don't need an id since this has its own change handler
  selectAllCheckbox: Omit<ICheckbox, 'id'>;
  onChange: (params: { id: string; checked: boolean }) => void;
  onSelectAllChange: (params: { allChecked: boolean }) => void;
}> = ({
  children,
  checkboxes,
  selectAllCheckbox,
  onChange,
  onSelectAllChange,
}) => {
  return (
    <div className="text-left py-1 font-bold">
      <Menu>
        <Menu.Button
          type="button"
          className={'relative flex space-x-1 items-center'}
        >
          <span>{children}</span>

          <TableFilterIconSvg
            wantsCurrentColor
            svg={{ className: 'w-4 text-gray-700' }}
          />
        </Menu.Button>

        <Menu.Items
          className={'absolute bg-white p-3 rounded shadow-12px space-y-2'}
        >
          <Menu.Item>
            <SelectAllCheckbox
              checked={selectAllCheckbox.checked}
              onChange={onSelectAllChange}
            >
              {selectAllCheckbox.children}
            </SelectAllCheckbox>
          </Menu.Item>

          {checkboxes.map(({ checked, children, id, disabled }) => {
            return (
              <Menu.Item key={id}>
                <S2Checkbox
                  checked={checked}
                  disabled={disabled}
                  onChange={() => {
                    onChange({ id, checked: !checked });
                  }}
                >
                  <span className="font-normal">{children}</span>
                </S2Checkbox>
              </Menu.Item>
            );
          })}
        </Menu.Items>
      </Menu>
    </div>
  );
};

export const TABLE_HEADER_OFFSET_HEIGHT = '40px';

const TableHeader = <T extends object>({
  columnDefinitions,
}: {
  columnDefinitions: ColumnDefinition<T>[];
}): React.ReactElement => {
  return (
    <thead className="bg-gray-100 sticky top-0 left-0 right-0">
      <tr>
        {columnDefinitions.map((columnDefinition, index) => {
          return (
            <th
              key={index}
              className={cn(
                'text-left py-1 px-2 font-bold',
                index === 0 && 'sticky left-0 bg-gray-100 z-[1]',
              )}
            >
              {columnDefinition.header()}
            </th>
          );
        })}
      </tr>
    </thead>
  );
};

// eslint-disable-next-line complexity
export const Table = <T extends object>({
  columnDefinitions,
  items,
  displayHeader = true,
  footer = null,
  empty = <div className="text-gray-900 text-center">No results</div>,
  loading = <Spinner />,
  isLoading = false,
  refs,
  classNames,
}: TableProps<T>): React.ReactElement => {
  const internalContainerRef = React.useRef<HTMLDivElement>(null);
  const scrollRef = React.useRef<HTMLDivElement>(null);

  const {
    lastRow: lastRowRef,
    tableContainer: tableContainerRef = internalContainerRef,
  } = refs ?? {};

  const isEmpty = items.length === 0;

  return (
    <div
      className={cn(
        classNames?.outerContainer,
        'relative',
        'overflow-x-auto',
        'w-full',
      )}
      ref={scrollRef}
    >
      <div
        className={cn(
          'border-2 border-white rounded-xl',
          classNames?.tableContainer,
          'relative',
          'min-w-full',
          'inline-block',
        )}
        ref={tableContainerRef}
      >
        <table className={cn('font-sans', 'w-full', 'relative')}>
          {displayHeader && (
            <TableHeader columnDefinitions={columnDefinitions} />
          )}

          <tbody>
            {isEmpty && !isLoading && (
              <EmptyTableRow colSpan={columnDefinitions.length}>
                {empty}
              </EmptyTableRow>
            )}

            {!isEmpty && (
              <TableRows
                items={items}
                columnDefinitions={columnDefinitions}
                lastRowRef={lastRowRef}
                className={classNames?.rows ?? 'even:bg-gray-100 odd:bg-white'}
              />
            )}

            {isLoading && (
              <LoadingTableRow colSpan={columnDefinitions.length}>
                {loading}
              </LoadingTableRow>
            )}
          </tbody>
        </table>
      </div>

      {footer && <div className="py-4">{footer}</div>}
    </div>
  );
};
